<template>
  <div class="kpi-producao">
    <div class="chart-box" v-if="servicosMensal && servicosMensal.length">
      <charts type="bar" :series="servicosSeries" :options="servicosOptions" height="280px" />
    </div>

    <div class="kpi-grid kpi-grid--5" v-if="cards">
      <StatsCard :value="fmtDias(cards.prazo_medio_entrega)" :label="t('Prazo médio OS')" icon="pi pi-calendar-clock" variant="info" compact />
      <StatsCard :value="fmtDec(cards.media_entrada_itens)" :label="t('Entrada itens/dia')" icon="pi pi-arrow-right" variant="primary" compact />
      <StatsCard :value="fmtDec(cards.media_producao_diaria)" :label="t('Produção diária')" icon="pi pi-cog" variant="success" compact />
      <StatsCard :value="fmtDec(cards.meta_producao_diaria)" :label="t('Meta produção')" icon="pi pi-flag" variant="warning" compact />
      <StatsCard :value="cards.itens_atrasados" :label="t('Itens atrasados')" icon="pi pi-exclamation-triangle" :variant="cards.itens_atrasados > 0 ? 'danger' : 'success'" compact />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-state">
      <p>{{ t('Erro ao carregar dados de produção') }}</p>
      <Button :label="t('Tentar novamente')" @click="fetchAll" data-testid="producao-kpi-retry" />
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="sk-grid">
      <DashboardSkeletonBlock height="80px" radius="10px" v-for="n in 5" :key="'p-'+n" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DashboardSkeletonBlock from '../../widgets/DashboardSkeletonBlock.vue';
import StatsCard from '../../../../ui/StatsCard.vue';
import { useDashboardProducaoKpi } from '../../../../composables/dashboard/useDashboardProducaoKpi';
import { useChartFormat } from '../../../../composables/useChartFormat';

const { t, n } = useI18n();
const { currencySymbol, monthLabel } = useChartFormat();
const { cards, servicosMensal, isLoading, error, fetchAll } = useDashboardProducaoKpi();

const loading = isLoading;

function fmtDec(v: number) { return n(v / 100, 'decimal'); }
function fmtDias(v: number) { return `${n(v / 100, 'decimal')} ${t('dias')}`; }

const servicosSeries = computed(() => {
  const ms = servicosMensal.value;
  if (!ms?.length) return [];

  const cats = new Set<string>();
  for (const m of ms) for (const c of m.categorias ?? []) cats.add(c.categoria);
  const sorted = Array.from(cats).sort();

  return sorted.map(nome => ({
    name: nome,
    data: ms.map(m => {
      const match = (m.categorias ?? []).find(c => c.categoria === nome);
      return match ? Math.round((match.valor ?? 0) / 100) : 0;
    }),
  }));
});

const servicosOptions = computed(() => ({
  chart: { stacked: true, toolbar: { show: true } },
  xaxis: { categories: servicosMensal.value?.map(m => monthLabel(m.mes)) ?? [], labels: { style: { fontSize: '11px' } } },
  yaxis: { title: { text: currencySymbol() }, labels: { style: { fontSize: '11px' } } },
  dataLabels: { enabled: false },
  tooltip: { shared: true, intersect: false, y: { formatter: (val: number) => n(val, 'currency') } },
  legend: { fontSize: '12px' },
}));
</script>

<style scoped>
.kpi-grid { display: grid; gap: 1.25rem; margin-top: 1.25rem; }
.kpi-grid--5 { grid-template-columns: repeat(5, 1fr); }
@media (max-width: 1100px) { .kpi-grid--5 { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 900px) { .kpi-grid--5 { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 500px) { .kpi-grid--5 { grid-template-columns: 1fr; } }

.chart-box { background: var(--p-surface-card); border: 1px solid var(--p-surface-border); border-radius: 8px; padding: 0.75rem; }

.sk-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1rem; }
@media (max-width: 900px) { .sk-grid { grid-template-columns: repeat(2, 1fr); } }

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: var(--p-text-color-secondary);
  text-align: center;
  gap: 1rem;
}
</style>
